import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import SubjectAutocomplete from "@/components/app/SubjectAutocomplete";
import { readApplicantExplicit, readApplicantProfile } from "@/lib/applicantProfile";
import { COUNTRIES } from "@/lib/countries";

const STEPS = [
  { key: "mobility", title: "Mobility & identity",
    intro: "Passport, residence and previous history. These change your visa route." },
  { key: "academic", title: "Academic profile",
    intro: "Your curriculum, grades and tests. Grades are translated across systems." },
  { key: "financial", title: "Financial picture",
    intro: "Ranges are fine — you'll refine later. This drives affordability + visa evidence checks." },
  { key: "preferences", title: "Personal & career",
    intro: "What matters to you and what the degree needs to lead to." },
  { key: "thrive", title: "Course DNA · will you thrive?",
    intro: "How you actually want to study day-to-day, and where you want your career to land. This becomes a 6th layer of the match." },
];

const empty = {
  passports: ["UK"],
  residence_country: "",
  residence_expiry_months: "",
  prior_visa_refusal: false,
  dependants_accompanying: false,
  preferred_countries: ["UK", "US", "Germany", "France", "Netherlands", "Ireland", "Italy", "Spain", "Switzerland", "Australia", "Canada", "Singapore", "United Arab Emirates", "India", "South Korea"],
  psw_importance: 4,
  start_months_away: "",
  age_years: "",
  study_gap_months: "",

  curriculum: "a_level",
  a_level: "",
  ib: "", cbse: "", american_diploma: "", gpa: "",
  atar: "",
  // New international curricula — routed through the cross-curriculum
  // translation layer in `ai_advisor.classify()`.
  isc: "", ap_composite: "", abitur: "", french_bac: "", hkdse: "", suneung: "",
  sat: "", act: "", ielts: "", toefl: "",
  // Per-subject grades (used by classify to enforce subject gates)
  ib_maths: "", ib_english: "",
  a_level_maths: "", a_level_english: "",
  activities_strength: 3,
  portfolio_interview_strength: 3,
  intended_degree: "",

  max_annual_tuition_usd: "",
  annual_living_budget_usd: "",
  total_family_budget_usd: "",
  max_deposit_usd: "",
  scholarship_required: false,
  funds_source: "family",

  campus_pref: "either",
  fit_weights: { prestige: 6, affordability: 6, career: 8, campus: 5, city: 6, sport: 3, diversity: 5 },
  accreditation_important: false,
  wants_placement: true,

  // 6th-layer inputs — "will you thrive here?"
  exam_vs_coursework_pref: 3,
  teaching_size_pref: 3,
  workload_intensity_pref: 3,
  curriculum_structure_pref: 3,
  dream_employers: [],
  settlement_intent: "unsure",
  first_gen: false,
};

// Every country where we currently index universities, ordered by
// coverage weight so the biggest options appear first. If a country
// stops being covered or a new one is added, bump this list.
// Source of truth: `db.universities.country` distinct values.
const COUNTRY_OPTS = [
  // Top-tier coverage (100+ universities)
  "US", "UK",
  // Mid-tier coverage (20+ universities)
  "Germany", "Canada", "China", "Netherlands", "Australia",
  "Finland", "Italy", "Malaysia", "France", "Taiwan", "Türkiye",
  "Spain", "United Arab Emirates",
  // Smaller coverage — still searchable
  "Denmark", "Saudi Arabia", "Switzerland", "India", "Singapore",
  "Austria", "Portugal", "South Korea", "Sweden", "Ireland", "Belgium",
  "New Zealand", "Norway", "Hong Kong", "Japan", "Iceland",
  "Estonia", "Czechia", "Poland",
];

export default function AdvancedWizard({ onSaved, initial, anonymous = false }) {
  const [step, setStep] = useState(0);
  // Priority order for the initial applicant snapshot:
  //   1. `initial` prop (server-persisted profile) — canonical, always wins.
  //   2. sessionStorage snapshot written by Quick Check — saves users
  //      from re-typing grades if they moved from /admission to /advanced.
  //   3. `empty` defaults.
  const _quickCheck = readApplicantProfile();
  const _hasQuickCheck = _quickCheck && Object.keys(_quickCheck).length > 0;
  const _seed = { ...empty, ...(_quickCheck || {}), ...(initial || {}) };
  // Normalise intended_degree — accepts either legacy string or new list.
  if (_seed.intended_degree && !Array.isArray(_seed.intended_degree)) {
    _seed.intended_degrees = [_seed.intended_degree];
  } else if (Array.isArray(_seed.intended_degree)) {
    _seed.intended_degrees = _seed.intended_degree;
  } else {
    _seed.intended_degrees = _seed.intended_degrees || [];
  }
  const [f, setF] = useState(_seed);
  // Track whether the initial state came from the Quick Check sync (and
  // NOT from a server-persisted profile) — we show a small "prefilled
  // from Quick Check" banner only in that case.
  const [prefilledFromQC] = useState(_hasQuickCheck && !initial);
  const [saving, setSaving] = useState(false);
  // Carry over only Quick Check fields the student actually entered (not its defaults),
  // and only where a saved server value isn't taking precedence.
  const [explicitFields, setExplicitFields] = useState(() => new Set(
    readApplicantExplicit()
      .filter((k) => !(initial && initial[k] !== undefined && initial[k] !== null && initial[k] !== ""))
      .map((k) => (k === "intended_degrees" ? "intended_degree" : k))
  ));
  const set = (k, v, explicitKey = k) => {
    setExplicitFields((prev) => new Set(prev).add(explicitKey));
    setF((p) => ({ ...p, [k]: v }));
  };

  const togglePref = (c) => {
    const cur = f.preferred_countries || [];
    set(
      "preferred_countries",
      cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c],
      `preferred_countries.${c}`
    );
  };
  const togglePassport = (c) => {
    const cur = f.passports || [];
    set("passports", cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Convert intended_degrees chips → intended_degree wire field.
      // Send string when 1 chip, array when >1 (backend accepts both).
      const packed = { ...f };
      const chips = (packed.intended_degrees || []).filter(Boolean);
      delete packed.intended_degrees;
      if (chips.length === 1) packed.intended_degree = chips[0];
      else if (chips.length > 1) packed.intended_degree = chips;
      else packed.intended_degree = "";
      const payload = Object.fromEntries(
        Object.entries(packed).map(([k, v]) => [k, v === "" ? null : v])
      );
      payload.explicit_fields = Array.from(explicitFields);
      payload.source_surface = "advanced_search";
      // Anonymous / free-trial mode — skip persistence, just hand the
      // packed profile to the parent (AdvancedSearch stashes it in
      // sessionStorage + AdvancedResults POSTs it to /match-anon).
      if (anonymous) {
        toast.success("Running your 3-layer match…");
        onSaved(payload);
        return;
      }
      const { data } = await api.post("/profile", payload);
      toast.success("Profile saved");
      onSaved(data.profile);
    } catch (e) {
      toast.error("Could not save profile");
    } finally { setSaving(false); }
  };

  const S = STEPS[step];

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10" data-testid="edit-profile-modal">
      <div className="label-uppercase">Advanced Search · Step {step + 1} of {STEPS.length}</div>
      <h1 className="font-editorial text-4xl mt-2">{S.title}</h1>
      <p className="mt-2 text-stone-600 text-sm">{S.intro}</p>
      <Progress value={((step + 1) / STEPS.length) * 100} className="mt-4" />

      {anonymous && (
        <div
          data-testid="wizard-anon-banner"
          className="mt-4 rounded-md border border-amber-300 bg-amber-50/60 p-3 text-[13px] text-stone-800 leading-snug"
        >
          <span className="font-semibold text-amber-900">Free trial · no sign-up.</span>
          {" "}Fill out the wizard and we'll run up to <span className="font-mono font-semibold">3</span> matches for you. Nothing is saved to an account until you sign in.
        </div>
      )}

      {(f.dream_employers || []).length > 0 && (
        <div
          data-testid="wizard-dream-employer-pinned"
          className="mt-4 rounded-md border border-emerald-300 bg-emerald-50/60 p-3 text-[13px] text-stone-800 leading-snug flex items-center justify-between gap-3"
        >
          <div>
            <span className="font-semibold text-emerald-900">Targeting:</span>{" "}
            {(f.dream_employers || []).join(" · ")}.{" "}
            Career-fit scoring will boost universities with a confirmed
            pipeline to {(f.dream_employers || []).length === 1 ? "this firm" : "these firms"}.
          </div>
          <button
            type="button"
            data-testid="wizard-dream-employer-clear"
            onClick={() => set("dream_employers", [])}
            className="shrink-0 text-[11px] font-underground-sc tracking-wider text-emerald-900 underline hover:no-underline"
            aria-label="Clear pinned employer"
          >
            Clear
          </button>
        </div>
      )}

      <Card className="mt-8 p-6 border-stone-200 bg-white">
        {S.key === "mobility" && (
          <div className="space-y-5">
            <div>
              <Label className="label-uppercase">Passports you hold</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {["UK", "US", "India", "UAE", "Pakistan", "Nigeria", "China", "EU (any)", "Other"].map((c) => (
                  <button key={c} type="button" data-testid={`passport-${c}`}
                          onClick={() => togglePassport(c)}
                          className={`px-3 py-1.5 rounded-md border text-sm ${
                            (f.passports || []).includes(c)
                              ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                              : "bg-white border-stone-300 hover:border-stone-500"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-uppercase">Country of legal residence</Label>
                <Input data-testid="wiz-residence-input" className="mt-1"
                       list="wiz-residence-countries"
                       placeholder="e.g. United Arab Emirates"
                       value={f.residence_country || ""}
                       onChange={(e) => set("residence_country", e.target.value)} />
                <datalist id="wiz-residence-countries">
                  {COUNTRIES.map((c) => (
                    <option key={c.value} value={c.label} />
                  ))}
                </datalist>
              </div>
              <div>
                <Label className="label-uppercase">Residence expires in (months)</Label>
                <Input data-testid="wiz-residence-exp-input" type="number" inputMode="numeric" className="mt-1 font-mono"
                       placeholder="e.g. 24"
                       value={f.residence_expiry_months}
                       onChange={(e) => set("residence_expiry_months", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between border border-stone-200 rounded-md p-3">
                <div className="text-sm">Previous visa refusal</div>
                <Switch data-testid="wiz-refusal-toggle" checked={!!f.prior_visa_refusal}
                        onCheckedChange={(v) => set("prior_visa_refusal", v)} />
              </div>
              <div className="flex items-center justify-between border border-stone-200 rounded-md p-3">
                <div className="text-sm">Dependants accompanying</div>
                <Switch data-testid="wiz-dependants-toggle" checked={!!f.dependants_accompanying}
                        onCheckedChange={(v) => set("dependants_accompanying", v)} />
              </div>
            </div>
            <div>
              <Label className="label-uppercase">Countries you'd consider</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {COUNTRY_OPTS.map((c) => (
                  <button key={c} type="button" data-testid={`pref-country-${c}`}
                          onClick={() => togglePref(c)}
                          className={`px-3 py-1.5 rounded-md border text-sm ${
                            (f.preferred_countries || []).includes(c)
                              ? "bg-[#002147] text-white border-[#002147]"
                              : "bg-white border-stone-300 hover:border-stone-500"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-uppercase">Start university in (months from now)</Label>
                <Input data-testid="wiz-start-input" type="number" inputMode="numeric" className="mt-1 font-mono"
                       placeholder="e.g. 12"
                       value={f.start_months_away}
                       onChange={(e) => set("start_months_away", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
              <div>
                <Label className="label-uppercase">Post-study work importance — {f.psw_importance}/5</Label>
                <Slider min={1} max={5} step={1} data-testid="wiz-psw-slider"
                        value={[f.psw_importance]} onValueChange={(v) => set("psw_importance", v[0])} className="mt-3" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-uppercase">Age</Label>
                <Input type="number" inputMode="numeric" className="mt-1 font-mono" data-testid="wiz-age-input"
                       placeholder="e.g. 18"
                       value={f.age_years}
                       onChange={(e) => set("age_years", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
              <div>
                <Label className="label-uppercase">Study gap (months)</Label>
                <Input type="number" inputMode="numeric" className="mt-1 font-mono" data-testid="wiz-gap-input"
                       placeholder="e.g. 0"
                       value={f.study_gap_months}
                       onChange={(e) => set("study_gap_months", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
            </div>
          </div>
        )}

        {S.key === "academic" && (
          <div className="space-y-5">
            {prefilledFromQC && (
              <div
                data-testid="wiz-prefilled-from-qc"
                className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900 flex items-center gap-2"
              >
                <Check className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                <span>
                  Prefilled from your <a href="/admission" className="underline hover:no-underline">Quick Check</a>.
                  Edit anything below — your changes stay in this session.
                </span>
              </div>
            )}
            <div>
              <Label className="label-uppercase">Curriculum</Label>
              <Select value={f.curriculum} onValueChange={(v) => set("curriculum", v)}>
                <SelectTrigger data-testid="wiz-curriculum-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="a_level">A Levels</SelectItem>
                  <SelectItem value="ib">IB Diploma</SelectItem>
                  <SelectItem value="atar">ATAR (Australia)</SelectItem>
                  <SelectItem value="cbse">CBSE (India, %)</SelectItem>
                  <SelectItem value="isc">ISC / CISCE (India, %)</SelectItem>
                  <SelectItem value="american_diploma">American Diploma (GPA)</SelectItem>
                  <SelectItem value="gpa">GPA (other)</SelectItem>
                  <SelectItem value="ap_composite">AP composite (US)</SelectItem>
                  <SelectItem value="abitur">Abitur (Germany)</SelectItem>
                  <SelectItem value="french_bac">Baccalauréat (France)</SelectItem>
                  <SelectItem value="hkdse">HKDSE (Hong Kong)</SelectItem>
                  <SelectItem value="suneung">Suneung / CSAT (South Korea)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {f.curriculum === "a_level" && (
              <div><Label className="label-uppercase">Predicted A-Levels</Label>
                <Input className="mt-1 font-mono" data-testid="wiz-alevel-input"
                       placeholder="e.g. A*AA"
                       value={f.a_level} onChange={(e) => set("a_level", e.target.value.toUpperCase())} /></div>
            )}
            {f.curriculum === "ib" && (
              <div><Label className="label-uppercase">Predicted IB total (24–45)</Label>
                <Input type="number" min="24" max="45" inputMode="numeric" className="mt-1 font-mono" data-testid="wiz-ib-input"
                       placeholder="e.g. 38"
                       value={f.ib} onChange={(e) => set("ib", e.target.value === "" ? "" : Number(e.target.value))} /></div>
            )}
            {f.curriculum === "ib" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-uppercase">Maths HL/SL grade (1–7)</Label>
                  <Input type="number" min="1" max="7" className="mt-1 font-mono" data-testid="wiz-ib-maths-input"
                         placeholder="e.g. 7"
                         value={f.ib_maths} onChange={(e) => set("ib_maths", e.target.value === "" ? "" : Number(e.target.value))} />
                  <div className="text-[10px] text-stone-500 mt-1">Required by CS / Engineering / Finance degrees.</div>
                </div>
                <div>
                  <Label className="label-uppercase">English HL/SL grade (1–7)</Label>
                  <Input type="number" min="1" max="7" className="mt-1 font-mono" data-testid="wiz-ib-english-input"
                         placeholder="e.g. 6"
                         value={f.ib_english} onChange={(e) => set("ib_english", e.target.value === "" ? "" : Number(e.target.value))} />
                  <div className="text-[10px] text-stone-500 mt-1">Optional — helps evaluate humanities degrees.</div>
                </div>
              </div>
            )}
            {f.curriculum === "a_level" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-uppercase">Maths A-level grade</Label>
                  <Input className="mt-1 font-mono" data-testid="wiz-alevel-maths-input"
                         placeholder="A* / A / B …"
                         value={f.a_level_maths} onChange={(e) => set("a_level_maths", e.target.value.toUpperCase())} />
                </div>
                <div>
                  <Label className="label-uppercase">English A-level grade</Label>
                  <Input className="mt-1 font-mono" data-testid="wiz-alevel-english-input"
                         placeholder="A* / A / B …"
                         value={f.a_level_english} onChange={(e) => set("a_level_english", e.target.value.toUpperCase())} />
                </div>
              </div>
            )}
            {f.curriculum === "cbse" && (
              <div><Label className="label-uppercase">CBSE overall %</Label>
                <Input type="number" min="0" max="100" inputMode="numeric" className="mt-1 font-mono" data-testid="wiz-cbse-input"
                       placeholder="e.g. 92"
                       value={f.cbse} onChange={(e) => set("cbse", e.target.value === "" ? "" : Number(e.target.value))} /></div>
            )}
            {f.curriculum === "american_diploma" && (
              <div><Label className="label-uppercase">American Diploma GPA</Label>
                <Input type="number" step="0.01" min="0" max="4" inputMode="decimal" className="mt-1 font-mono" data-testid="wiz-adgpa-input"
                       placeholder="e.g. 3.8"
                       value={f.american_diploma} onChange={(e) => set("american_diploma", e.target.value === "" ? "" : Number(e.target.value))} /></div>
            )}
            {f.curriculum === "gpa" && (
              <div><Label className="label-uppercase">GPA</Label>
                <Input type="number" step="0.01" min="0" max="4" inputMode="decimal" className="mt-1 font-mono" data-testid="wiz-gpa-input"
                       placeholder="e.g. 3.7"
                       value={f.gpa} onChange={(e) => set("gpa", e.target.value === "" ? "" : Number(e.target.value))} /></div>
            )}
            {f.curriculum === "atar" && (
              <div><Label className="label-uppercase">ATAR (0.00–99.95)</Label>
                <Input type="number" step="0.05" min="0" max="99.95" inputMode="decimal" className="mt-1 font-mono"
                       data-testid="wiz-atar-input" placeholder="e.g. 90"
                       value={f.atar} onChange={(e) => set("atar", e.target.value === "" ? "" : Number(e.target.value))} /></div>
            )}
            {f.curriculum === "isc" && (
              <div><Label className="label-uppercase">ISC overall % (0–100)</Label>
                <Input type="number" step="0.5" min="0" max="100" inputMode="decimal" className="mt-1 font-mono"
                       data-testid="wiz-isc-input" placeholder="e.g. 88"
                       value={f.isc} onChange={(e) => set("isc", e.target.value === "" ? "" : Number(e.target.value))} /></div>
            )}
            {f.curriculum === "ap_composite" && (
              <div><Label className="label-uppercase">AP composite (1.0–5.0)</Label>
                <Input type="number" step="0.05" min="1" max="5" inputMode="decimal" className="mt-1 font-mono"
                       data-testid="wiz-ap-input" placeholder="e.g. 4.2"
                       value={f.ap_composite} onChange={(e) => set("ap_composite", e.target.value === "" ? "" : Number(e.target.value))} /></div>
            )}
            {f.curriculum === "abitur" && (
              <div><Label className="label-uppercase">Abitur average (1.0 best → 4.0 pass)</Label>
                <Input type="number" step="0.1" min="1.0" max="4.0" inputMode="decimal" className="mt-1 font-mono"
                       data-testid="wiz-abitur-input" placeholder="e.g. 1.7"
                       value={f.abitur} onChange={(e) => set("abitur", e.target.value === "" ? "" : Number(e.target.value))} /></div>
            )}
            {f.curriculum === "french_bac" && (
              <div><Label className="label-uppercase">Baccalauréat (0–20)</Label>
                <Input type="number" step="0.5" min="0" max="20" inputMode="decimal" className="mt-1 font-mono"
                       data-testid="wiz-bac-input" placeholder="e.g. 15"
                       value={f.french_bac} onChange={(e) => set("french_bac", e.target.value === "" ? "" : Number(e.target.value))} /></div>
            )}
            {f.curriculum === "hkdse" && (
              <div><Label className="label-uppercase">HKDSE best-5 sum (0–35)</Label>
                <Input type="number" step="1" min="0" max="35" inputMode="numeric" className="mt-1 font-mono"
                       data-testid="wiz-hkdse-input" placeholder="e.g. 28"
                       value={f.hkdse} onChange={(e) => set("hkdse", e.target.value === "" ? "" : Number(e.target.value))} /></div>
            )}
            <div>
              <Label className="label-uppercase">Intended subject / degree</Label>
              <div className="mt-1">
                <SubjectAutocomplete
                  values={f.intended_degrees || []}
                  onChange={(vs) => set("intended_degrees", vs)}
                  placeholder="e.g. Computer Science, Religious Studies, PPE"
                  testIdPrefix="wiz-degree-picker"
                  maxChips={5}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-uppercase">SAT (blank if not taken)</Label>
                <Input type="number" className="mt-1 font-mono" data-testid="wiz-sat-input"
                       value={f.sat} onChange={(e) => set("sat", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
              <div>
                <Label className="label-uppercase">ACT (blank if not taken)</Label>
                <Input type="number" className="mt-1 font-mono" data-testid="wiz-act-input"
                       value={f.act} onChange={(e) => set("act", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
              <div>
                <Label className="label-uppercase">IELTS (blank if not taken)</Label>
                <Input type="number" step="0.5" className="mt-1 font-mono" data-testid="wiz-ielts-input"
                       value={f.ielts} onChange={(e) => set("ielts", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
              <div>
                <Label className="label-uppercase">TOEFL (blank if not taken)</Label>
                <Input type="number" className="mt-1 font-mono" data-testid="wiz-toefl-input"
                       value={f.toefl} onChange={(e) => set("toefl", e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
            </div>
            <div>
              <Label className="label-uppercase">Activities / leadership strength — {f.activities_strength}/5</Label>
              <Slider min={1} max={5} step={1} data-testid="wiz-activities-slider"
                      value={[f.activities_strength]} onValueChange={(v) => set("activities_strength", v[0])} className="mt-3" />
            </div>
          </div>
        )}

        {S.key === "financial" && (
          <div className="space-y-5">
            <p className="text-xs text-stone-500">
              All figures in <strong>US dollars</strong>. Ranges are fine — use round numbers your family
              has actually agreed on. You can display results in your local currency
              on the results page.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-uppercase" htmlFor="wiz-max-tuition-input">Max tuition — per year (USD)</Label>
                <Input id="wiz-max-tuition-input" type="number" inputMode="numeric"
                       className="mt-1 font-mono"
                       placeholder="e.g. 40,000"
                       data-testid="wiz-max-tuition-input"
                       value={f.max_annual_tuition_usd}
                       onChange={(e) => set("max_annual_tuition_usd", e.target.value === "" ? "" : Number(e.target.value))} />
                <p className="mt-1 text-xs text-stone-500">
                  The highest tuition sticker price you'd consider <strong>for one academic year</strong>. Excludes living costs.
                </p>
              </div>
              <div>
                <Label className="label-uppercase" htmlFor="wiz-max-living-input">Max living costs — per year (USD)</Label>
                <Input id="wiz-max-living-input" type="number" inputMode="numeric"
                       className="mt-1 font-mono"
                       placeholder="e.g. 18,000"
                       data-testid="wiz-max-living-input"
                       value={f.annual_living_budget_usd}
                       onChange={(e) => set("annual_living_budget_usd", e.target.value === "" ? "" : Number(e.target.value))} />
                <p className="mt-1 text-xs text-stone-500">
                  Rent, food, transport, insurance, books, phone — <strong>everything except tuition</strong>, for one year.
                </p>
              </div>
              <div>
                <Label className="label-uppercase" htmlFor="wiz-total-budget-input">Total family budget — whole degree (USD)</Label>
                <Input id="wiz-total-budget-input" type="number" inputMode="numeric"
                       className="mt-1 font-mono"
                       placeholder="e.g. 220,000"
                       data-testid="wiz-total-budget-input"
                       value={f.total_family_budget_usd}
                       onChange={(e) => set("total_family_budget_usd", e.target.value === "" ? "" : Number(e.target.value))} />
                <p className="mt-1 text-xs text-stone-500">
                  The absolute ceiling your family can spend across the <strong>full degree
                  duration</strong> (typically 3 years in the UK / 4 years in the US). Tuition +
                  living + travel + fee inflation — everything, minus expected scholarships.
                </p>
              </div>
              <div>
                <Label className="label-uppercase" htmlFor="wiz-deposit-input">Max upfront deposit (USD)</Label>
                <Input id="wiz-deposit-input" type="number" inputMode="numeric"
                       className="mt-1 font-mono"
                       placeholder="e.g. 5,000"
                       data-testid="wiz-deposit-input"
                       value={f.max_deposit_usd}
                       onChange={(e) => set("max_deposit_usd", e.target.value === "" ? "" : Number(e.target.value))} />
                <p className="mt-1 text-xs text-stone-500">
                  Non-refundable deposit you're willing to pay <strong>before</strong> classes start. Usually 5–15% of Year 1 tuition.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between border border-stone-200 rounded-md p-3">
              <div className="text-sm">I need a scholarship to make this work</div>
              <Switch data-testid="wiz-scholarship-toggle" checked={!!f.scholarship_required}
                      onCheckedChange={(v) => set("scholarship_required", v)} />
            </div>
            <div>
              <Label className="label-uppercase">Source of funds</Label>
              <Select value={f.funds_source} onValueChange={(v) => set("funds_source", v)}>
                <SelectTrigger className="mt-1" data-testid="wiz-funds-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="family">Family savings / earnings</SelectItem>
                  <SelectItem value="savings">Own savings</SelectItem>
                  <SelectItem value="loan">Education loan</SelectItem>
                  <SelectItem value="third_party">Third-party sponsor</SelectItem>
                </SelectContent>
              </Select>
              {(f.funds_source === "loan" || f.funds_source === "third_party") && (
                <p className="mt-2 text-xs text-amber-800">
                  Loan and third-party funding often need extra visa documentation. We'll flag this in your results.
                </p>
              )}
            </div>
          </div>
        )}

        {S.key === "preferences" && (
          <div className="space-y-5">
            <div>
              <Label className="label-uppercase">Campus preference</Label>
              <ToggleGroup type="single" value={f.campus_pref} onValueChange={(v) => v && set("campus_pref", v)}
                           className="mt-2 grid grid-cols-3 gap-1" data-testid="wiz-campus-pref-toggle">
                <ToggleGroupItem value="city" className="border border-stone-200">City-integrated</ToggleGroupItem>
                <ToggleGroupItem value="campus" className="border border-stone-200">Campus-based</ToggleGroupItem>
                <ToggleGroupItem value="either" className="border border-stone-200">Either</ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div>
              <div className="label-uppercase mb-2">What matters most (0–10 each)</div>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(f.fit_weights).map(([k, v]) => (
                  <div key={k}>
                    <div className="label-uppercase !text-[10px]">{k} — {v}</div>
                    <Slider min={0} max={10} step={1} data-testid={`wiz-weight-${k}`}
                            value={[v]} onValueChange={(nv) => set("fit_weights", { ...f.fit_weights, [k]: nv[0] }, `fit_weights.${k}`)}
                            className="mt-2" />
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between border border-stone-200 rounded-md p-3">
                <div className="text-sm">Professional accreditation matters</div>
                <Switch data-testid="wiz-accred-toggle" checked={!!f.accreditation_important}
                        onCheckedChange={(v) => set("accreditation_important", v)} />
              </div>
              <div className="flex items-center justify-between border border-stone-200 rounded-md p-3">
                <div className="text-sm">I want a placement year</div>
                <Switch data-testid="wiz-placement-toggle" checked={!!f.wants_placement}
                        onCheckedChange={(v) => set("wants_placement", v)} />
              </div>
            </div>
          </div>
        )}

        {S.key === "thrive" && (
          <div className="space-y-6">
            {/* Course DNA sliders */}
            <div>
              <div className="label-uppercase mb-3">How you actually want to study</div>
              <div className="space-y-5">
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-500">Exams</span>
                    <span className="font-mono text-xs text-[#4C1D95]">{f.exam_vs_coursework_pref}/5</span>
                    <span className="text-stone-500">Coursework</span>
                  </div>
                  <Slider data-testid="wiz-dna-exam" min={1} max={5} step={1}
                          value={[f.exam_vs_coursework_pref]}
                          onValueChange={(v) => set("exam_vs_coursework_pref", v[0])}
                          className="mt-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-500">Big lectures</span>
                    <span className="font-mono text-xs text-[#4C1D95]">{f.teaching_size_pref}/5</span>
                    <span className="text-stone-500">Small tutorials</span>
                  </div>
                  <Slider data-testid="wiz-dna-size" min={1} max={5} step={1}
                          value={[f.teaching_size_pref]}
                          onValueChange={(v) => set("teaching_size_pref", v[0])}
                          className="mt-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-500">Work-life balance</span>
                    <span className="font-mono text-xs text-[#4C1D95]">{f.workload_intensity_pref}/5</span>
                    <span className="text-stone-500">Research grind</span>
                  </div>
                  <Slider data-testid="wiz-dna-workload" min={1} max={5} step={1}
                          value={[f.workload_intensity_pref]}
                          onValueChange={(v) => set("workload_intensity_pref", v[0])}
                          className="mt-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-500">Fixed structure</span>
                    <span className="font-mono text-xs text-[#4C1D95]">{f.curriculum_structure_pref}/5</span>
                    <span className="text-stone-500">Pick my modules</span>
                  </div>
                  <Slider data-testid="wiz-dna-structure" min={1} max={5} step={1}
                          value={[f.curriculum_structure_pref]}
                          onValueChange={(v) => set("curriculum_structure_pref", v[0])}
                          className="mt-2" />
                </div>
              </div>
              <div className="text-xs text-stone-500 mt-3 font-mono">
                Leave everything at 3/5 to skip this layer — degrees will not be penalised.
              </div>
            </div>

            {/* Dream employers */}
            <div>
              <Label className="label-uppercase">Dream employers or grad schools (optional)</Label>
              <div className="text-xs text-stone-500 mt-1 mb-2">
                Any name or category — Goldman Sachs, McKinsey, Google, NHS, BBC, med school, PhD. Substring-matched against each degree's top-employer list.
              </div>
              <SubjectAutocomplete
                values={f.dream_employers || []}
                onChange={(vs) => set("dream_employers", vs)}
                placeholder="Type an employer, then press Enter or comma"
                suggestEndpoint={null}
                testIdPrefix="wiz-dream-employers"
                maxChips={5}
              />
            </div>

            {/* Portfolio / interview strength */}
            <div>
              <Label className="label-uppercase">Portfolio / interview strength — {f.portfolio_interview_strength}/5</Label>
              <Slider data-testid="wiz-interview-strength" min={1} max={5} step={1}
                      value={[f.portfolio_interview_strength]}
                      onValueChange={(v) => set("portfolio_interview_strength", v[0])}
                      className="mt-3" />
              <div className="text-xs text-stone-500 mt-1 font-mono">
                1 = untested · 3 = solid draft/mocks · 5 = published portfolio or MMI-ready. Nudges interview-heavy admits (Oxbridge / Med / Architecture) up or down.
              </div>
            </div>

            {/* Settlement intent */}
            <div>
              <Label className="label-uppercase">After graduation, do you want to stay in the destination country?</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[
                  { v: "stay", label: "Yes, I want to stay" },
                  { v: "unsure", label: "Not sure yet" },
                  { v: "leave", label: "No, I'll go home" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`wiz-settlement-${opt.v}`}
                    onClick={() => set("settlement_intent", opt.v)}
                    className={`px-3 py-2 rounded-md text-sm border transition ${
                      f.settlement_intent === opt.v
                        ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                        : "bg-white border-stone-300 hover:border-stone-500 text-stone-700"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="text-xs text-stone-500 mt-2">
                Saying "yes" boosts Shortage-Occupation degrees and long post-study-work jurisdictions.
              </div>
            </div>

            {/* First-gen toggle */}
            <div className="flex items-center justify-between border border-stone-200 rounded-md p-3">
              <div>
                <div className="text-sm">I'm the first in my family to go to university</div>
                <div className="text-xs text-stone-500">Surfaces universities with strong first-gen mentoring and access programmes.</div>
              </div>
              <Switch data-testid="wiz-first-gen-toggle" checked={!!f.first_gen}
                      onCheckedChange={(v) => set("first_gen", v)} />
            </div>
          </div>
        )}
      </Card>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                data-testid="wiz-prev-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={() => setStep((s) => s + 1)} data-testid="wiz-next-btn"
                  className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md">
            Next <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={save} disabled={saving} data-testid="wiz-save-btn"
                  className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md">
            {saving ? "Saving…" : (
              <>
                <Check className="h-4 w-4 mr-1" />
                {anonymous ? "Run my 3 free matches" : "Save profile & run 5-layer match"}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
